import type { PlanCode, Root } from "./prototypeTypes";
import { byId, setText } from "./domUtils";
import { escapeHtml } from "./formatUtils";
import { ensureTopic, getTopic, type TopicRecord } from "./topicStore";
import { getTopicNavigationChapterCount, getTopicNavigationChapterPreview, getTopicNavigationPreview } from './topicNavigationPreview';
import { readPageModel } from './pageModelReader';
import { paintArticleNavVisibility, resetArticleNavigation } from "./articleNavVisibility";
import { paintArticleBody } from './articleBodyPainter';
import { refreshMediaSessionPlayer, syncArticleAudioButtonFromRuntime } from "./mediaPlayer";
import { showView, type ViewHydrationOptions } from "./navigationController";
import { paintArticleMeta } from "./articleMetaPainter";
import { clearStaleInertUnderRoot } from "./modalAccessibility";
import type { ChapterApiResponse } from "./articleControllerTypes";
import { getRuntime } from "./runtimeAccessor";
import { isPaidPlan } from "./controllerState";
import { navigateWhenSsrViewMissing } from './ssrDirectNavigation';
import { paintContentTraceLabel } from './contentTraceLabel';
import { disposeKeywordCloud, renderKeywordCloudSkeleton } from './keywordCloud';
import { syncChapterDocumentSeo } from './courseDocumentSeo';
import { showToast } from './toastController';
import { coverUrlForTopic, categoriesFromClassification } from '../../domain/topicCover';
import { applyTopicCover } from './topicCoverLoader';
import { nextArticleTopicHistoryState } from './topicArticleHistory';
import { beginChapterProgressIntent, recordChapterProgress, rollbackChapterProgressIntent, type ChapterProgressIntent } from './chapterProgressTracker';
import { recoverTopicReturnAfterProgressRollback } from './topicReturnReconciler';
import { PLAY_ICON_INNER } from '../../shared/playerIcons';
import { articleBodySkeletonLinesMarkup, articleSummarySkeletonLinesMarkup } from '../../shared/articleLoadingMarkup';

const articleRequestGenerations = new WeakMap<object, number>();

function nextArticleRequestGeneration(root: Root): number {
	const generation = (articleRequestGenerations.get(root) ?? 0) + 1;
	articleRequestGenerations.set(root, generation);
	return generation;
}

export function showArticle(num: number, root: Root, plan: PlanCode = "free", options: ViewHydrationOptions & { recordProgress?: boolean; deferChapterLoad?: boolean } = {}) {
	const requestGeneration = nextArticleRequestGeneration(root);
	// Defensive inert reset: a previous modal or dock flow could have left
	// `.app-layout` / `#view-article` inert (modal snapshot never restored,
	// dock destroyed mid-flight, etc.). The article view must be interactive
	// for the Listen/Watch pills and chapter controls to register clicks.
	clearStaleInertUnderRoot(root);
  const activeSlug = getRuntime(root)?.activeCourseId;
  if (activeSlug && navigateWhenSsrViewMissing(root, 'article', `/topics/${encodeURIComponent(activeSlug)}/chapters/${num}`)) return;
	const sourceView = root.querySelector<HTMLElement>('.view.active')?.id.replace(/^view-/u, '') ?? null;
	const targetPath = activeSlug ? `/topics/${encodeURIComponent(activeSlug)}/chapters/${num}` : '/article';
	const normalizePath = (path: string) => path.replace(/\/+$/u, '') || '/';
	const historyState = activeSlug
		? nextArticleTopicHistoryState(window.history.state, {
			sourceView,
			topicSlug: activeSlug,
			pushesRoute: normalizePath(window.location.pathname) !== normalizePath(targetPath),
		})
		: {};
	const routeHistoryState = { ...historyState, ...options.historyState };
  const index = Number.isFinite(num) && num > 0 ? num - 1 : 0;
  const runtime = getRuntime(root);
  if (runtime) {
    runtime.currentPlan = plan;
    // Keep the runtime chapter index in sync so the prev / next
    // action-row capsules (and the article-bottom navigation)
    // have a single source of truth. Without this, deep-linking
    // directly into /topics/:slug/chapters/N left the runtime
    // stuck on chapter 1 and the prev/next pills went silent.
    runtime.activeChapterIndex = num;
  }
  const activeSlugForArticle = runtime?.activeCourseId;
	const requestViewerScope = viewerScopeFor(runtime);
	const progressIntent = options.recordProgress && runtime && activeSlugForArticle
		? beginChapterProgressIntent(runtime, activeSlugForArticle, num)
		: null;
	if (runtime && activeSlugForArticle) syncChapterDocumentSeo(runtime.api, activeSlugForArticle, num);
	const api = runtime?.api;
	const indexedTopic = activeSlugForArticle ? getTopic(activeSlugForArticle) : undefined;
	const previewTopic = !indexedTopic && activeSlugForArticle ? getTopicNavigationPreview(root, activeSlugForArticle) : undefined;
	const topic = indexedTopic ?? previewTopic;
  const chapters = indexedTopic ? [...indexedTopic.previewChapters, ...indexedTopic.lockedChapters] : [];
  const indexedChapter = chapters.find((chapter) => chapter.index === num);
  const previewChapter = activeSlugForArticle ? getTopicNavigationChapterPreview(root, activeSlugForArticle, num) : undefined;
  // A partial SSR seed may omit inactive or unrenderable entries. Never borrow another row's
  // title: the chapter list preview is exact-index-only and has already been
  // rendered on this Topic page.
  const title = indexedChapter?.title ?? previewChapter?.title ?? '';
	const knownSummary = activeSlugForArticle ? articleSummaryForExactChapter(root, activeSlugForArticle, num) : undefined;
	primeArticleShell(root, plan, num, title, topic?.title ?? "", activeSlugForArticle, topic?.classification, topic?.coverImageUrl, topic?.subtitle, knownSummary);
	showView("article", root, { historyState: routeHistoryState, historyMode: options.historyMode });
	// Recent cards may need a metadata validation round before loading body
	// content. They still enter this exact Article route immediately; the
	// caller re-enters without a history write once the opaque chapter id has
	// been confirmed.
	if (options.deferChapterLoad) return;
	if (!topic) {
		if (api && activeSlugForArticle) {
			void loadDirectChapter(api, activeSlugForArticle, num, plan, root, runtime, requestGeneration, requestViewerScope, progressIntent);
			return;
		}
		setText(root, "article-title", "Chapter unavailable");
		paintArticleError(root);
    return;
  }

	if (api && activeSlugForArticle) {
    api.getChapter(activeSlugForArticle, num, plan)
			.then((response) => {
				if (!isOwnedArticleRequest(root, runtime, activeSlugForArticle, num, requestGeneration, requestViewerScope)) return;
        const r = response as ChapterApiResponse | null;
				if (!r?.chapter?.id) {
					recoverProgressIntentAfterUnavailableChapter(root, activeSlugForArticle, plan, runtime, progressIntent);
					if (isArticleViewActive(root)) paintArticleError(root);
					return;
				}
				if (options.recordProgress && runtime) {
					void recordChapterProgress(runtime, {
						topicId: activeSlugForArticle,
						chapterId: r.chapter.id,
						chapterIndex: r.chapter.index ?? num,
						status: 'in_progress',
						progressPercent: 0,
					}, progressIntent ?? undefined).catch(() => {
						recoverProgressIntentAfterUnavailableChapter(root, activeSlugForArticle, plan, runtime, progressIntent);
					});
				}
				if (!isArticleViewActive(root)) return;
				rememberArticleSummary(root, activeSlugForArticle, num, r.chapter?.summary);
        paintArticleBody(root, r);
        paintArticleMeta(root, r, chapterDirectoryCount(root, activeSlugForArticle));
        paintArticleNext(root, r, activeSlugForArticle);
        paintContentTraceLabel(byId(root, 'article-publication-info'), r.protection?.traceCode, 'article', { topicSource: r.course?.source });
        // P2.7: 删除 triggerChapterPaywall 自动弹窗逻辑（spec §6.6 要求）
        // 页面加载时不弹登录和付费墙，只有用户点击 Gate CTA 才触发
        // MeteredContentGate 渐变 + CTA 仍由 paintPaywall 渲染
			})
			.catch(() => {
				if (!isOwnedArticleRequest(root, runtime, activeSlugForArticle, num, requestGeneration, requestViewerScope)) return;
				recoverProgressIntentAfterUnavailableChapter(root, activeSlugForArticle, plan, runtime, progressIntent);
				if (isArticleViewActive(root)) paintArticleError(root);
			});
  } else {
    paintArticleError(root);
  }
}

/**
 * A private Topic requires its guarded, owner-scoped Chapter API for direct
 * Chapter URLs. The direct read remains independent from navigation metadata
 * so an unavailable Topic projection never becomes a false not-found page.
 */
async function loadDirectChapter(
	api: NonNullable<ReturnType<typeof getRuntime>>['api'],
	slug: string,
	chapterIndex: number,
	plan: PlanCode,
	root: Root,
	runtime: ReturnType<typeof getRuntime>,
	requestGeneration: number,
	requestViewerScope: string,
	progressIntent: ChapterProgressIntent | null,
): Promise<void> {
	const topicIndex = ensureTopic(slug).then(() => true).catch(() => false);
	try {
		const response = await api.getChapter(slug, chapterIndex, plan) as ChapterApiResponse | null;
		if (!isOwnedArticleRequest(root, runtime, slug, chapterIndex, requestGeneration, requestViewerScope)) return;
		if (!response?.chapter?.id) throw new Error('chapter_unavailable');
		const resolvedIndex = response.chapter.index ?? chapterIndex;
		if (runtime) runtime.activeChapterIndex = resolvedIndex;
		if (progressIntent && runtime && response.chapter.id) {
			void recordChapterProgress(runtime, {
				topicId: slug,
				chapterId: response.chapter.id,
				chapterIndex: resolvedIndex,
				status: 'in_progress',
				progressPercent: 0,
			}, progressIntent).catch(() => {
				recoverProgressIntentAfterUnavailableChapter(root, slug, plan, runtime, progressIntent);
			});
		}
		if (!isArticleViewActive(root)) return;
		paintArticleHeroText(root, 'article-title', response.chapter.title ?? 'Chapter', false);
		paintChapterHeroTitleAffixes(root, resolvedIndex, response.chapter.duration);
		paintArticleHeroText(root, 'article-topic-context', response.course?.title ?? 'Private topic', false);
		rememberArticleSummary(root, slug, chapterIndex, response.chapter?.summary);
		const heroBg = root.querySelector<HTMLElement>('#chapter-hero > .topic-poster-cover');
		if (heroBg && response.course?.coverImageUrl) void applyTopicCover(heroBg, response.course.coverImageUrl);
		paintArticleBody(root, response);
		paintArticleMeta(root, response, chapterDirectoryCount(root, slug));
		paintArticleNext(root, response, slug);
		paintContentTraceLabel(byId(root, 'article-publication-info'), response.protection?.traceCode, 'article', { topicSource: response.course?.source });
		void topicIndex.then((available) => {
			if (!available || articleRequestGenerations.get(root) !== requestGeneration) return;
			if (runtime?.activeCourseId !== slug || runtime.activeChapterIndex !== resolvedIndex) return;
			if (!isArticleViewActive(root)) return;
			if (!getTopic(slug)) return;
			paintArticleNext(root, response, slug);
			// On a cold deep link the chapter total is only known once the topic
			// index resolves; repaint the hero metric so "Chapter 7" gains its
			// "of 8" instead of overwriting the SSR-painted total with the index.
			paintArticleMeta(root, response, chapterDirectoryCount(root, slug));
		});
	} catch {
		if (!isOwnedArticleRequest(root, runtime, slug, chapterIndex, requestGeneration, requestViewerScope)) return;
		recoverProgressIntentAfterUnavailableChapter(root, slug, plan, runtime, progressIntent);
		if (!isArticleViewActive(root)) return;
		setText(root, 'article-title', 'Chapter unavailable');
		paintArticleError(root);
	}
}

function isOwnedArticleRequest(
	root: Root,
	runtime: ReturnType<typeof getRuntime>,
	slug: string,
	chapterIndex: number,
	requestGeneration: number,
	requestViewerScope: string,
): boolean {
	return articleRequestGenerations.get(root) === requestGeneration
		&& runtime?.activeCourseId === slug
		&& runtime.activeChapterIndex === chapterIndex
		&& viewerScopeFor(runtime) === requestViewerScope;
}

function isArticleViewActive(root: Root): boolean {
	return root.querySelector('#view-article')?.classList.contains('active') === true;
}

/** Chapter total for the hero metric ("7 of 8"); 0 while the store is cold. */
function chapterDirectoryCount(root: Root, slug: string): number {
	const previewCount = getTopicNavigationChapterCount(root, slug);
	if (previewCount !== undefined) return previewCount;
	const ssrCount = pageModelChapterCount(slug);
	if (ssrCount !== undefined) return ssrCount;
	const topic = getTopic(slug);
	return topic ? [...topic.previewChapters, ...topic.lockedChapters].length : 0;
}

/**
 * A direct SSR Topic keeps its complete directory in the PageModel after
 * auth hydration invalidates the Topic navigation store. Only reuse the exact
 * Topic identity; Home-page arrays and mismatched former routes are never a
 * substitute for this total.
 */
function pageModelChapterCount(slug: string): number | undefined {
	const model = readPageModel();
	if (!model || model.topic.slug !== slug) return undefined;
	const directory = model.pageType === 'topic' ? model.chapters : model.directory;
	if (!Array.isArray(directory)) return undefined;
	const count = directory.length;
	return Number.isInteger(count) && count > 0 ? count : undefined;
}

/**
 * Reuse only the cached record for the chapter that is entering. This gives
 * the loading hero stable geometry without letting a previous chapter's
 * duration or copy bleed into the new route while its API response is pending.
 */
function cachedChapterHeroContext(root: Root, slug: string | undefined, chapterIndex: number): { total?: number; duration?: string; subtitle?: string } {
	if (!slug) return {};
	const preview = getTopicNavigationChapterPreview(root, slug, chapterIndex);
	const total = chapterDirectoryCount(root, slug) || undefined;
	if (preview) return { total, duration: preview.duration, subtitle: preview.subtitle };
	const topic = getTopic(slug);
	if (!topic || (topic.slug !== slug && topic.id !== slug)) return total ? { total } : {};
	const chapters = [...topic.previewChapters, ...topic.lockedChapters];
	const chapter = chapters.find((candidate) => candidate.index === chapterIndex);
	if (!chapter) return { total };
	// `subtitle` is optional in the navigation projection. Read it only
	// when a future cache payload provides it, and only from this exact chapter.
	const subtitle = (chapter as typeof chapter & { subtitle?: unknown }).subtitle;
	return {
		total,
		duration: chapter.duration.trim() || undefined,
		subtitle: typeof subtitle === 'string' && subtitle.trim() ? subtitle.trim() : undefined,
	};
}

const articleSummariesByRoot = new WeakMap<object, Map<string, string>>();

function articleSummaryKey(root: Root, slug: string, chapterIndex: number): string {
	return `${viewerScopeFor(getRuntime(root))}|${slug}#${chapterIndex}`;
}

function rememberArticleSummary(root: Root, slug: string, chapterIndex: number, summary: unknown): void {
	if (!slug || !Number.isInteger(chapterIndex) || chapterIndex < 1) return;
	const text = typeof summary === 'string' ? summary.trim() : '';
	let summaries = articleSummariesByRoot.get(root);
	if (!summaries) {
		summaries = new Map();
		articleSummariesByRoot.set(root, summaries);
	}
	// An empty string is meaningful: the exact Chapter is known not to have an
	// Overview, and must not flash a speculative card before the response ends.
	summaries.set(articleSummaryKey(root, slug, chapterIndex), text);
}

/**
 * Only a successful response can seed this cache. The current URL may advance
 * before old Article DOM is replaced, so DOM text alone is never evidence that
 * a visible Overview belongs to the Chapter being entered.
 */
function articleSummaryForExactChapter(root: Root, slug: string, chapterIndex: number): string | undefined {
	const summaries = articleSummariesByRoot.get(root);
	const key = articleSummaryKey(root, slug, chapterIndex);
	return summaries?.has(key) ? summaries.get(key) : undefined;
}

function viewerScopeFor(runtime: ReturnType<typeof getRuntime>): string {
	return `${runtime?.userId ?? ''}|${runtime?.sessionHash ?? ''}`;
}

function recoverProgressIntentAfterUnavailableChapter(
	root: Root,
	slug: string,
	plan: PlanCode,
	runtime: ReturnType<typeof getRuntime>,
	intent: ChapterProgressIntent | null,
): void {
	if (!runtime || !intent) return;
	const rollback = rollbackChapterProgressIntent(runtime, intent);
	if (!rollback.rolledBack) return;
	const topic = getTopic(slug);
	if (topic) recoverTopicReturnAfterProgressRollback(root, topic, plan, runtime);
}

function primeArticleShell(
	root: Root,
	plan: PlanCode,
	num: number,
	title: string,
	topicTitle: string,
	activeSlug?: string,
	classification?: TopicRecord['classification'],
	coverImageUrl?: string,
	topicSubtitle?: string,
	knownSummary?: string,
) {
  byId(root, "view-article")?.setAttribute("data-viewer", isPaidPlan(plan) ? "paid" : "preview");
  resetArticleNavigation(root);
  // Chapter hero banner shares the topic's cover image (resolved by the same
  // unified coverUrlForTopic). Set inline so SPA navigation to a chapter
  // shows the correct category cover immediately, matching SSR output.
  const heroBg = root.querySelector<HTMLElement>('#chapter-hero > .topic-poster-cover');
  if (heroBg) {
		void applyTopicCover(heroBg, coverImageUrl ?? coverUrlForTopic(categoriesFromClassification(classification)));
  }
  const skeleton = byId(root, "article-skeleton");
  const rendered = byId(root, "article-body-rendered");
  const errorBox = byId(root, "article-body-error");
  // SSR may arrive with an empty #article-skeleton after the server has
  // rendered a ready body. SPA chapter navigation clears that body before its
  // next response, so reinsert the same grouped placeholder used by SSR.
  if (skeleton) {
    skeleton.innerHTML = articleBodySkeletonLinesMarkup;
    skeleton.hidden = false;
  }
  if (rendered) { rendered.hidden = true; rendered.innerHTML = ""; }
  if (errorBox) errorBox.hidden = true;
	const summary = byId(root, 'article-summary');
	const cachedContext = cachedChapterHeroContext(root, activeSlug, num);
	if (cachedContext.total || cachedContext.duration) {
		paintArticleMeta(root, { chapter: { index: num, duration: cachedContext.duration } }, cachedContext.total);
	}
	// The route itself and its identity-matched topic cache are already known.
	// Render those facts immediately; retain grouped skeletons solely for data
	// we do not have yet, instead of clearing the entire hero on every chapter
	// transition.
	paintChapterHeroTitleAffixes(root, num, cachedContext.duration);
	paintArticleHeroText(root, 'article-topic-context', topicTitle, !topicTitle, 1);
	paintArticleHeroText(root, 'article-title', title, !title, 2);
	paintArticleHeroText(root, 'article-summary', cachedContext.subtitle ?? '', !cachedContext.subtitle, 2);
	if (summary) summary.hidden = false;
	const summaryCopy = byId(root, 'article-summary-copy');
	const summaryPanel = byId(root, 'article-summary-panel');
	const informationBand = byId(root, 'chapter-info-band');
	if (knownSummary === '') {
		// This exact Chapter was already resolved without an Overview. Do not
		// invent a card that will disappear when the fresh body response returns.
		if (summaryCopy) {
			summaryCopy.classList.remove('article-summary-loading');
			summaryCopy.replaceChildren();
		}
		if (summaryPanel) summaryPanel.hidden = true;
		if (informationBand) informationBand.hidden = true;
	} else {
		const preservedSummary = knownSummary?.trim() ?? '';
		if (summaryCopy) {
			summaryCopy.classList.toggle('article-summary-loading', !preservedSummary);
			summaryCopy.innerHTML = preservedSummary ? escapeHtml(preservedSummary) : articleSummarySkeletonLinesMarkup;
		}
		if (summaryPanel) {
			// Keep the real disclosure card in flow while the body request is pending.
			// A matching cached summary stays readable; otherwise its own copy area
			// reserves the same cadence with shimmer instead of moving the body up.
			summaryPanel.hidden = false;
			(summaryPanel as HTMLDetailsElement).open = true;
		}
		if (informationBand) informationBand.hidden = false;
	}
	informationBand?.classList.remove('chapter-info-band--single');
	const takeaways = byId(root, 'article-takeaways');
	if (takeaways) {
		takeaways.hidden = true;
		(takeaways as HTMLDetailsElement).open = false;
		takeaways.innerHTML = '<summary>Learning outcomes</summary><ul></ul>';
	}
	const keywordCloud = byId(root, 'chapter-keyword-cloud');
	disposeKeywordCloud(keywordCloud);
	if (keywordCloud) {
		// Sphere-shaped skeleton so the reserved keyword box keeps the same
		// silhouette/height as the real cloud. paintChapterKeywordCloud will
		// renderKeywordCloud() over it when the API resolves.
		renderKeywordCloudSkeleton(keywordCloud);
	}
	const keywordRegion = byId(root, 'chapter-keywords-region');
	if (keywordRegion) keywordRegion.hidden = false;
	const actionLoading = byId(root, 'article-hero-actions-loading');
	if (actionLoading) {
		actionLoading.hidden = false;
		actionLoading.parentElement?.classList.add('chapter-actions--loading');
	}
	resetArticleMediaActions(root);
	['article-faq', 'article-references'].forEach((id) => {
		const element = byId(root, id);
		if (element) { element.hidden = true; element.innerHTML = ''; }
	});
	const publicationInfo = byId(root, 'article-publication-info');
	if (publicationInfo) { publicationInfo.hidden = true; publicationInfo.replaceChildren(); }
  byId(root, "article-body")?.setAttribute("data-state", "loading");
  const paywall = byId(root, "article-paywall");
  if (paywall) paywall.hidden = true;
  // Instead of hiding the metadata row (which causes a jarring flash when the
  // API resolves and the metrics re-appear), apply a chapter-specific skeleton
  // class. We don't reuse tdb-metric--loading because that sets a fixed 92px
  // width designed for the topic hero banner metrics; the chapter hero metrics
  // have <b>label + value children whose width varies. This class just makes
  // the text transparent + applies shimmer, preserving natural width.
	[
		['article-meta-chapter', Boolean(cachedContext.total)],
		['article-meta-duration', Boolean(cachedContext.duration)],
	].forEach(([id, known]) => {
		const el = byId(root, id as string);
		if (!el) return;
		el.hidden = false;
		el.classList.toggle('chapter-metric--loading', !known);
	});
  // The audio button is rendered exclusively by
  // syncArticleAudioButtonFromRuntime(root) below, so we no longer
  // pre-burn an "Audio" + triangle SVG here. The previous
  // unconditional reset produced a one-frame flash on every entry
  // when playback was already running because the runtime-sync
  // re-painted the pause icon immediately after.
	const meteredTail = byId(root, 'article-metered-tail');
	if (meteredTail) { meteredTail.hidden = true; meteredTail.innerHTML = ''; }
  byId(root, "articleVideoBtn")?.setAttribute("data-proto-click", "triggerVideoPaywall()");
  byId(root, "articleAudioBtn")?.setAttribute("data-proto-click", "playArticleAudio()");
  (root as unknown as { __activeChapter?: { index: number; title: string; topicTitle: string; topicSlug: string; topicSubtitle: string } }).__activeChapter = {
    index: num,
    title,
    topicTitle,
    topicSlug: activeSlug ?? '',
    topicSubtitle: topicSubtitle ?? '',
  };
	refreshMediaSessionPlayer(root, 'audio');
	refreshMediaSessionPlayer(root, 'video');
  // Sync-paint the action-row prev/next capsules from the cached
  // topic store so the user sees the navigation the moment the
  // article view appears, instead of waiting for the chapter
  // network call to resolve. The bottom nav cards (with chapter
  // titles + duration) are still painted by paintArticleNext
  // when the chapter response returns.
  const slugForNav = activeSlug;
  if (slugForNav) paintArticleNavVisibility(root, slugForNav, num);
  syncArticleAudioButtonFromRuntime(root);
}

function paintArticleNext(root: Root, response: ChapterApiResponse, slug: string) {
  const prevList = byId(root, "article-prev-list");
  const nextList = byId(root, "article-next-list");
  if (!prevList || !nextList) return;
	// A direct Chapter route may resolve before its topic directory. Keep the
	// stale navigation cleared until that directory arrives; otherwise a
	// synthetic Back-to-Topic card flashes for a topic whose identity is not
	// yet trustworthy.
	if (!getTopic(slug)) return;
  const runtime = getRuntime(root);
  const currentIndex = typeof response.chapter?.index === "number"
    ? response.chapter.index
    : typeof runtime?.activeChapterIndex === "number"
      ? runtime.activeChapterIndex
      : 1;
  const { prev, next } = paintArticleNavVisibility(root, slug, currentIndex);
  if (prev) {
    prevList.innerHTML = renderChapterNav(slug, prev, 'Previous');
  } else {
    prevList.innerHTML = "";
  }

  nextList.innerHTML = next ? renderChapterNav(slug, next, 'Next') : renderChapterBackToTopic(slug);
}

function renderChapterNav(slug: string, chapter: { index: number; title: string; duration?: string }, label: 'Previous' | 'Next'): string {
	const arrow = label === 'Next' ? '<svg class="chapter-nav-arrow" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"></path><path d="m13 6 6 6-6 6"></path></svg>' : '';
	return `<a class="chapter-nav-card chapter-nav-${label.toLowerCase() === 'previous' ? 'prev chapter-nav-card--secondary' : 'next chapter-nav-card--primary'}" href="/topics/${encodeURIComponent(slug)}/chapters/${chapter.index}" data-proto-click="showArticle(${chapter.index})"><span class="chapter-nav-row chapter-nav-row--meta" data-chapter-nav-row="meta"><span data-chapter-nav-label>${label}</span><span data-chapter-nav-duration>${escapeHtml(chapter.duration?.trim() || '-')}</span></span><span class="chapter-nav-row chapter-nav-row--title" data-chapter-nav-row="title"><span data-chapter-nav-title>${escapeHtml(chapter.title)}</span></span>${arrow}</a>`;
}

function renderChapterBackToTopic(slug: string): string {
	return `<a class="chapter-nav-card chapter-nav-back chapter-nav-card--primary" href="/topics/${encodeURIComponent(slug)}" data-proto-click="backToActiveTopic()"><span class="chapter-nav-row chapter-nav-row--meta" data-chapter-nav-row="meta"><span data-chapter-nav-label>All chapters</span></span><span class="chapter-nav-row chapter-nav-row--title" data-chapter-nav-row="title"><span>Back to Topic</span></span><svg class="chapter-nav-arrow" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"></path><path d="m13 6 6 6-6 6"></path></svg></a>`;
}

function paintArticleError(root: Root) {
  const rendered = byId(root, "article-body-rendered");
  const errorBox = byId(root, "article-body-error");
  const skeleton = byId(root, "article-skeleton");
  if (skeleton) skeleton.hidden = true;
  if (rendered) { rendered.hidden = true; rendered.innerHTML = ""; }
	if (errorBox) errorBox.hidden = false;
	const summaryCopy = byId(root, 'article-summary-copy');
	if (summaryCopy) {
		summaryCopy.classList.remove('article-summary-loading');
		summaryCopy.replaceChildren();
	}
	const summaryPanel = byId(root, 'article-summary-panel');
	if (summaryPanel) summaryPanel.hidden = true;
	const informationBand = byId(root, 'chapter-info-band');
	if (informationBand) {
		informationBand.hidden = true;
		informationBand.classList.remove('chapter-info-band--single');
	}
	paintArticleHeroText(root, 'article-topic-context', 'Topic unavailable', false);
	paintChapterHeroTitleAffixes(root, 0);
	paintArticleHeroText(root, 'article-title', 'Chapter unavailable', false);
	paintArticleHeroText(root, 'article-summary', 'We could not load this chapter. Please try again.', false);
	for (const id of ['article-meta-chapter', 'article-meta-duration']) {
		const metric = byId(root, id);
		if (!metric) continue;
		metric.classList.remove('chapter-metric--loading');
		metric.hidden = true;
	}
	const actionLoading = byId(root, 'article-hero-actions-loading');
	actionLoading?.setAttribute('hidden', '');
	actionLoading?.parentElement?.classList.remove('chapter-actions--loading');
	resetArticleMediaActions(root);
	const cloud = byId(root, 'chapter-keyword-cloud');
	disposeKeywordCloud(cloud);
	cloud?.replaceChildren();
	cloud?.classList.remove('kw-cloud-3d', 'kw-cloud--skeleton', 'topic-keyword-cloud--static');
	const keywordRegion = byId(root, 'chapter-keywords-region');
	if (keywordRegion) keywordRegion.hidden = false;
	byId(root, "article-body")?.setAttribute("data-state", "error");
}

function paintArticleHeroText(root: Root, id: string, value: string, loadingWhenEmpty: boolean, lineCount = 1): void {
	const element = byId(root, id);
	if (!element) return;
	const text = value.trim();
	element.classList.remove('topic-hero-loading');
	if (text || !loadingWhenEmpty) {
		element.textContent = text;
		return;
	}
	element.classList.add('topic-hero-loading');
	element.replaceChildren(...Array.from({ length: lineCount }, (_, index) => createArticleHeroLoadingLine(index === lineCount - 1 ? '58%' : '100%')));
}

/**
 * Chapter hero title affixes: "Ch.N:" prefix and duration suffix render via
 * CSS attr() pseudo-elements, so #article-title textContent stays the bare
 * chapter title (mini-bar liveTitle and tests read it verbatim). A zero or
 * negative index clears the prefix; an empty duration clears the suffix.
 */
function paintChapterHeroTitleAffixes(root: Root, chapterIndex: number, duration?: string): void {
	const title = byId(root, 'article-title');
	if (!title) return;
	if (Number.isInteger(chapterIndex) && chapterIndex > 0) title.dataset.chapterPrefix = `Ch.${chapterIndex}:`;
	else delete title.dataset.chapterPrefix;
	const trimmedDuration = duration?.trim();
	if (trimmedDuration) title.dataset.contextDuration = trimmedDuration;
	else delete title.dataset.contextDuration;
}

function createArticleHeroLoadingLine(width: string): HTMLElement {
	const line = document.createElement('span');
	line.className = 'topic-hero-loading-line';
	line.setAttribute('aria-hidden', 'true');
	// Only width is inline; height/margin come from CSS so the subtitle bars
	// inside .chapter-hero-subtitle can match the real 1lh line box (the
	// 0.72em stroke read as a thin sliver next to the 42px reserved slot).
	line.style.cssText = `display:block;width:${width};`;
	return line;
}

function resetArticleMediaActions(root: Root): void {
	const audio = byId(root, 'articleAudioBtn');
	if (audio) {
		audio.removeAttribute('hidden');
		audio.classList.add('is-disabled');
		audio.setAttribute('aria-disabled', 'true');
		audio.setAttribute('tabindex', '-1');
		audio.removeAttribute('data-chapter-index');
		audio.setAttribute('aria-label', 'Play chapter audio');
		audio.setAttribute('aria-pressed', 'false');
		const label = audio.querySelector<HTMLElement>('.article-audio-label');
		if (label) label.textContent = 'Listen';
		const icon = audio.querySelector<HTMLElement>('.article-audio-icon');
		if (icon) icon.innerHTML = PLAY_ICON_INNER;
	}
	const video = byId(root, 'articleVideoBtn');
	if (video) {
		video.removeAttribute('hidden');
		video.classList.add('is-disabled');
		video.setAttribute('aria-disabled', 'true');
		video.setAttribute('tabindex', '-1');
		video.removeAttribute('data-chapter-index');
	}
}

/**
 * Previous / Next chapter navigation for the article landing page.
 *
 * These actions mirror the article-action-row capsules added in
 * articleMarkup (4.3 / 3.x request). The lookup is purely
 * client-side: we read the active slug + chapter from the runtime
 * and the topic store, then call showArticle() to re-render.
 *
 * They are idempotent and a no-op when there is no prev/next
 * chapter, so the UI hides the button rather than disable it
 * (paintArticleNavCapsules handles that visibility).
 */
export async function previousArticleChapter(root: Root, plan: PlanCode = "free"): Promise<void> {
  const runtime = getRuntime(root);
  const slug = runtime?.activeCourseId;
  if (!slug) return;
  let topic = getTopic(slug);
  if (!topic) {
    try { await ensureTopic(slug); } catch { /* fall through */ }
    topic = getTopic(slug);
  }
  const current = runtime?.activeChapterIndex;
  if (!topic || !Number.isFinite(current) || (current as number) <= 0) return;
  const chapters = [...topic.previewChapters, ...topic.lockedChapters]
    .filter((chapter) => chapter.index < (current as number))
    .sort((a, b) => b.index - a.index);
  const prev = chapters[0];
  if (!prev) {
    // The transport keys are a constant row: a boundary click keeps its slot
    // and answers with a hint instead of collapsing or silently no-opping.
    showToast({ type: 'info', message: 'This is the first chapter.' });
    return;
  }
  showArticle(prev.index, root, plan);
}

export async function nextArticleChapter(root: Root, plan: PlanCode = "free", isLoggedIn = false): Promise<void> {
  const runtime = getRuntime(root);
  const slug = runtime?.activeCourseId;
  if (!runtime || !slug) return;
	const activeChapterIndex = runtime.activeChapterIndex;
	const isCurrentNavigation = () => runtime.activeCourseId === slug
		&& runtime.activeChapterIndex === activeChapterIndex
		&& root.querySelector('#view-article')?.classList.contains('active') === true;
  // Always read from a warmed topicStore. Cold-cache deep links
  // previously saw getTopic() return undefined and the next click
  // silently dropped, leaving the user on the same chapter even
  // though the button looked active.
  let topic = getTopic(slug);
  if (!topic) {
    try { await ensureTopic(slug); } catch { /* fall through */ }
		if (!isCurrentNavigation()) return;
    topic = getTopic(slug);
  }
  const current = runtime?.activeChapterIndex;
  if (!topic || !Number.isFinite(current) || (current as number) <= 0) return;
  const chapters = [...topic.previewChapters, ...topic.lockedChapters]
    .filter((chapter) => chapter.index > (current as number))
    .sort((a, b) => a.index - b.index);
  const next = chapters[0];
  if (!next) {
    showToast({ type: 'info', message: 'This is the last chapter.' });
    return;
  }
  if (!isCurrentNavigation()) return;
  showArticle(next.index, root, plan, { recordProgress: isLoggedIn });
}
